import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import api from '../services/api';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Copy, Share2, ShoppingCart, Trash2, Check, Printer, List } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import EstimateCard from './EstimateCard';

const fmt = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;

const EstimateDetailPopup = ({ open, onOpenChange, estimate, onDeleted, onDuplicate, onConfirmed }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [deleting, setDeleting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const cardRef = useRef(null);

  if (!estimate) return null;

  const handleDelete = async () => {
    if (!window.confirm(`Delete ${estimate.estimate_number}?`)) return;
    setDeleting(true);
    try {
      await api.delete(`/estimates/${estimate.estimate_number}`);
      toast({ title: 'Estimate deleted' });
      onOpenChange(false);
      if (onDeleted) onDeleted(estimate);
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' }); }
    finally { setDeleting(false); }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await api.patch(`/estimates/${estimate.estimate_number}/confirm`);
      toast({ title: 'Estimate confirmed' });
      if (onConfirmed) onConfirmed(estimate);
      onOpenChange(false);
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' }); }
    finally { setConfirming(false); }
  };

  const handleCreateOrder = () => {
    const stoneDetailsText = estimate.stones?.length > 0
      ? estimate.stones.map(s => `${s.name || 'Stone'}: ${s.weight}ct @ ₹${s.rate}/ct`).join(', ') : '';
    navigate('/messenger', {
      state: {
        fromEstimate: true,
        estimateData: {
          customerPhone: estimate.customer_phone,
          customerName: estimate.customer_name,
          mainGroup: estimate.main_group || '',
          title: estimate.product || '',
          productWeight: estimate.gross_weight?.toString() || '',
          karatPurity: estimate.gold_purity || '',
          goldRatePerGram: estimate.gold_rate?.toString() || '',
          goldRateType: 'fixed',
          stoneDetails: stoneDetailsText,
          priceAmount: estimate.total_amount?.toString() || '',
          linkedEstimateId: estimate.id,
          estimateNumber: estimate.estimate_number,
        },
      },
    });
  };

  const captureCard = async () => {
    // Give one tick for the hidden card to render
    await new Promise(r => setTimeout(r, 150));
    if (!cardRef.current) return null;
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
      });
      return canvas;
    } catch (e) {
      console.error('html2canvas error:', e);
      return null;
    }
  };

  const generatePrintHTML = (est) => {
    const fmtAmt = (n) => `&#x20B9;${Math.round(n || 0).toLocaleString('en-IN')}`;
    const raw = est.created_at || '';
    const dateStr = new Date(raw + (raw.includes('Z') || raw.includes('+') ? '' : 'Z'))
      .toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
    const makingLabel = est.making_charges_type === 'fixed'
      ? `Fixed charge`
      : `${fmtAmt(est.making_charges_per_gram)}/g &times; ${(est.net_weight || 0).toFixed(2)}g`;
    const stonesRows = (est.stones || []).map((s, i) => `
      <tr style="background:${i % 2 === 0 ? '#fff' : '#faf5ff'}">
        <td style="padding:7px 10px;border-bottom:1px solid #ede9fe;">${s.name || 'Stone'}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #ede9fe;text-align:center;">${s.weight}ct</td>
        <td style="padding:7px 10px;border-bottom:1px solid #ede9fe;text-align:right;">${fmtAmt(s.rate)}/ct</td>
        <td style="padding:7px 10px;border-bottom:1px solid #ede9fe;text-align:right;font-weight:700;">${fmtAmt(s.amount)}</td>
      </tr>`).join('');
    const summaryRows = [
      { label: 'Gold Amount', value: est.gold_amount },
      ...(est.stones_amount > 0 ? [{ label: 'Stone Amount', value: est.stones_amount }] : []),
      { label: 'Making Charges', value: est.making_amount },
      ...(est.other_charges > 0 ? [{ label: 'Other Charges', value: est.other_charges }] : []),
    ].map(r => `
      <tr>
        <td style="padding:8px 14px;border-bottom:1px solid #e5e7eb;font-size:13px;">${r.label}</td>
        <td style="padding:8px 14px;border-bottom:1px solid #e5e7eb;text-align:right;font-weight:600;font-size:13px;">${fmtAmt(r.value)}</td>
      </tr>`).join('');

    return `<!DOCTYPE html>
<html><head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>Estimate ${est.estimate_number}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Segoe UI',Arial,sans-serif;color:#1a1a1a;background:#f0f0f0;padding:16px}
  .page{background:#fff;max-width:780px;margin:0 auto;border-radius:6px;overflow:hidden;box-shadow:0 2px 16px rgba(0,0,0,.12)}
  .hdr{background:#064e3b;color:#fff;padding:16px 20px;display:flex;justify-content:space-between;align-items:flex-start}
  .shop-name{font-size:18px;font-weight:800;letter-spacing:2px}
  .shop-sub{font-size:9px;opacity:.7;margin-top:2px;letter-spacing:1px;text-transform:uppercase}
  .status{display:inline-block;margin-top:5px;padding:2px 10px;border-radius:20px;font-size:9px;font-weight:700;text-transform:uppercase}
  .confirmed{background:#34d399;color:#064e3b}.draft{background:#fbbf24;color:#78350f}
  .est-num{font-size:14px;font-weight:700;text-align:right}
  .est-date{font-size:10px;opacity:.8;text-align:right;margin-top:3px}
  .sec{padding:12px 20px 0}
  .cust-grid{display:grid;grid-template-columns:1fr 1fr;gap:5px 14px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;padding:10px 12px}
  .fl{font-size:11px;color:#6b7280}.fv{font-size:12px;font-weight:600;color:#111;margin-top:1px}
  .wrow{display:grid;grid-template-columns:repeat(4,1fr);gap:7px}
  .wbox{text-align:center;border:1px solid #d1fae5;border-radius:6px;padding:7px 4px;background:#f0fdf4}
  .wl{font-size:9px;color:#6b7280;text-transform:uppercase;letter-spacing:.4px}
  .wv{font-size:13px;font-weight:700;color:#064e3b;margin-top:2px}
  .rbox{display:flex;justify-content:space-between;align-items:center;border:1px solid #e5e7eb;border-radius:6px;padding:9px 12px}
  .rl{font-size:12px;font-weight:600}.rv{font-size:15px;font-weight:800}
  .rsub{font-size:10px;margin-top:2px}
  table{width:100%;border-collapse:collapse;font-size:12px}
  thead th{background:#f3f4f6;padding:6px 10px;text-align:left;font-size:9px;text-transform:uppercase;letter-spacing:.5px;color:#6b7280;border-bottom:2px solid #e5e7eb}
  .ttl-row td{padding:12px 14px;background:#064e3b;color:#fff;font-size:15px;font-weight:800;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .sumtbl{border:1px solid #e5e7eb;border-radius:6px;overflow:hidden}
  .ftr{padding:10px 20px 14px;text-align:center;border-top:1px solid #f0f0f0;margin-top:12px}
  @page{size:A4 portrait;margin:10mm 12mm}
  @media print{
    body{background:#fff;padding:0}
    .page{border-radius:0;box-shadow:none;max-width:100%}
    .hdr{-webkit-print-color-adjust:exact;print-color-adjust:exact}
    .ttl-row td{-webkit-print-color-adjust:exact;print-color-adjust:exact}
    .wbox{border:1px solid #999 !important;background:#f5f5f5 !important}
    .wv{color:#000 !important}
  }
</style>
</head>
<body><div class="page">
  <div class="hdr">
    <div>
      <div class="shop-name">DIAFORGE</div>
      <div class="shop-sub">Jewellery Estimates</div>
      <span class="status ${est.status === 'confirmed' ? 'confirmed' : 'draft'}">${est.status === 'confirmed' ? 'Confirmed' : 'Draft'}</span>
    </div>
    <div>
      <div class="est-num">${est.estimate_number}</div>
      <div class="est-date">${dateStr}</div>
    </div>
  </div>

  <div class="sec">
    <div class="cust-grid">
      <div><div class="fl">Customer</div><div class="fv">${est.customer_name || '—'}</div></div>
      <div><div class="fl">Phone</div><div class="fv">${est.customer_phone || '—'}</div></div>
      ${est.tag_number ? `<div><div class="fl">Tag No.</div><div class="fv">${est.tag_number}</div></div>` : ''}
      ${(est.main_group || est.product) ? `<div><div class="fl">Product</div><div class="fv">${[est.main_group, est.product].filter(Boolean).join(' - ')}</div></div>` : ''}
      ${est.style_name ? `<div><div class="fl">Style</div><div class="fv">${est.style_name}</div></div>` : ''}
      ${est.hsn_code ? `<div><div class="fl">HSN Code</div><div class="fv">${est.hsn_code}</div></div>` : ''}
      ${est.gold_purity ? `<div><div class="fl">Purity</div><div class="fv">${est.gold_purity}</div></div>` : ''}
    </div>
  </div>

  ${(est.images && est.images.length > 0) ? `
  <div class="sec" style="margin-top:10px">
    <div style="display:flex;justify-content:center;background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;padding:12px">
      <img src="${est.images[0]}" alt="Product"
        style="max-width:180px;max-height:160px;object-fit:cover;border-radius:6px;border:1px solid #e5e7eb"
        crossorigin="anonymous" />
    </div>
  </div>` : ''}

  <div class="sec" style="margin-top:10px">
    <div class="wrow">
      <div class="wbox"><div class="wl">Gross Wt</div><div class="wv">${est.gross_weight}g</div></div>
      <div class="wbox"><div class="wl">Stone Wt</div><div class="wv">${(est.stones_weight_carats || 0).toFixed(2)}ct</div></div>
      <div class="wbox"><div class="wl">Net Wt</div><div class="wv">${(est.net_weight || 0).toFixed(2)}g</div></div>
      <div class="wbox"><div class="wl">Wastage</div><div class="wv">${est.wastage_percentage}%</div></div>
    </div>
  </div>

  <div class="sec" style="margin-top:10px">
    <div class="rbox">
      <div>
        <div class="rl" style="color:#92400e">Gold Amount</div>
        <div class="rsub" style="color:#b45309">Rate: ${fmtAmt(est.gold_rate)}/g</div>
      </div>
      <div class="rv" style="color:#92400e">${fmtAmt(est.gold_amount)}</div>
    </div>
  </div>

  ${(est.stones && est.stones.length > 0) ? `
  <div class="sec" style="margin-top:10px">
    <div style="font-size:10px;font-weight:700;color:#6b21a8;text-transform:uppercase;letter-spacing:.5px;margin-bottom:6px">Diamonds / Stones (${est.stones.length})</div>
    <div style="border:1px solid #e9d5ff;border-radius:6px;overflow:hidden">
      <table>
        <thead><tr>
          <th>Description</th><th style="text-align:center">Weight</th>
          <th style="text-align:right">Rate</th><th style="text-align:right">Amount</th>
        </tr></thead>
        <tbody>${stonesRows}</tbody>
        <tfoot><tr style="background:#ede9fe">
          <td colspan="3" style="padding:7px 10px;font-size:11px;font-weight:700;color:#6b21a8">Stones Total</td>
          <td style="padding:7px 10px;text-align:right;font-size:13px;font-weight:800;color:#6b21a8">${fmtAmt(est.stones_amount)}</td>
        </tr></tfoot>
      </table>
    </div>
  </div>` : ''}

  <div class="sec" style="margin-top:10px">
    <div class="rbox">
      <div>
        <div class="rl" style="color:#9a3412">Making Charges</div>
        <div class="rsub" style="color:#c2410c">${makingLabel}</div>
      </div>
      <div class="rv" style="color:#9a3412">${fmtAmt(est.making_amount)}</div>
    </div>
  </div>

  ${est.other_charges > 0 ? `
  <div class="sec" style="margin-top:8px">
    <div class="rbox">
      <div class="rl" style="color:#374151">Other Charges</div>
      <div class="rv" style="color:#374151;font-size:13px">${fmtAmt(est.other_charges)}</div>
    </div>
  </div>` : ''}

  <div class="sec" style="margin-top:12px">
    <div class="sumtbl">
      <table>
        <tbody>${summaryRows}</tbody>
        <tfoot><tr class="ttl-row">
          <td>GRAND TOTAL (All Inclusive)</td>
          <td style="text-align:right">${fmtAmt(est.total_amount)}</td>
        </tr></tfoot>
      </table>
    </div>
  </div>

  <div class="ftr"><span style="font-size:10px;color:#9ca3af">Computer generated estimate &bull; DiaForge &bull; ${dateStr}</span></div>
</div>
<script>
window.onload = function() {
  var page = document.querySelector('.page');
  var contentH = page.scrollHeight;
  var A4_H = 1047;
  if (contentH > A4_H) {
    var zoom = (A4_H / contentH).toFixed(4);
    page.style.zoom = zoom;
  }
  window.print();
}
</script>
</body></html>`;
  };

  const handlePrint = () => {
    // Open window synchronously within the tap/click event — avoids pop-up blockers on iOS/Android
    const win = window.open('about:blank', '_blank');
    if (!win) {
      toast({ title: 'Pop-up blocked', description: 'Please allow pop-ups in your browser settings, then try again', variant: 'destructive' });
      return;
    }
    const html = generatePrintHTML(estimate);
    win.document.open();
    win.document.write(html);
    win.document.close();
  };

  const handleShare = async () => {
    setIsSharing(true);
    try {
      const canvas = await captureCard();
      if (!canvas) { toast({ title: 'Could not generate image', variant: 'destructive' }); return; }
      const filename = `estimate-${estimate.estimate_number}.jpg`;
      const blob = await new Promise(r => canvas.toBlob(r, 'image/jpeg', 0.95));
      const file = new File([blob], filename, { type: 'image/jpeg' });

      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'Estimate' });
      } else {
        // Fallback: download
        const url = canvas.toDataURL('image/jpeg', 0.95);
        const link = document.createElement('a');
        link.download = filename;
        link.href = url;
        link.click();
        toast({ title: 'Image downloaded' });
      }
    } catch (e) {
      if (e.name !== 'AbortError') toast({ title: 'Share failed', variant: 'destructive' });
    } finally {
      setIsSharing(false);
    }
  };

  const handleViewEstimates = () => {
    onOpenChange(false);
    navigate('/estimates');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="estimate-detail-popup">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{estimate.estimate_number}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${estimate.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
              {estimate.status}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Product & Customer */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-gray-500">Customer:</span> <span className="font-medium">{estimate.customer_name}</span></div>
            <div><span className="text-gray-500">Phone:</span> <span className="font-medium">{estimate.customer_phone}</span></div>
            {estimate.tag_number && <div><span className="text-gray-500">Tag:</span> <span className="font-medium text-blue-600">{estimate.tag_number}</span></div>}
            {(estimate.main_group || estimate.product) && (
              <div><span className="text-gray-500">Product:</span> <span className="font-medium">{[estimate.main_group, estimate.product].filter(Boolean).join(' - ')}</span></div>
            )}
            {estimate.style_name && (
              <div><span className="text-gray-500">Style:</span> <span className="font-medium">{estimate.style_name}</span></div>
            )}
            {estimate.hsn_code && (
              <div><span className="text-gray-500">HSN:</span> <span className="font-medium">{estimate.hsn_code}</span></div>
            )}
            <div><span className="text-gray-500">Date:</span> <span className="font-medium">{new Date(estimate.created_at).toLocaleDateString('en-IN')}</span></div>
            <div><span className="text-gray-500">Purity:</span> <span className="font-medium">{estimate.gold_purity}</span></div>
          </div>

          {/* Weights */}
          <div className="grid grid-cols-4 gap-2">
            {[
              { l: 'Gross', v: `${estimate.gross_weight}g` },
              { l: 'Stone', v: `${estimate.stones_weight_carats?.toFixed(2) || 0}ct` },
              { l: 'Net', v: `${estimate.net_weight?.toFixed(2) || 0}g` },
              { l: 'Wastage', v: `${estimate.wastage_percentage}%` },
            ].map((item, i) => (
              <div key={i} className="text-center bg-gray-50 rounded-lg p-2">
                <div className="text-[10px] text-gray-500 uppercase">{item.l}</div>
                <div className="text-sm font-bold">{item.v}</div>
              </div>
            ))}
          </div>

          {/* Gold */}
          <div className="flex justify-between items-center bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <span className="text-sm text-amber-800">Gold ({fmt(estimate.gold_rate)}/g)</span>
            <span className="font-bold text-amber-900">{fmt(estimate.gold_amount)}</span>
          </div>

          {/* Stones */}
          {estimate.stones?.length > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-1">
              <p className="text-xs font-semibold text-purple-700">Stones ({estimate.stones.length})</p>
              {estimate.stones.map((s, i) => (
                <div key={i} className="flex justify-between text-xs">
                  <span className="text-gray-600">{s.name}: {s.weight}ct @ {fmt(s.rate)}/ct</span>
                  <span className="font-medium text-purple-700">{fmt(s.amount)}</span>
                </div>
              ))}
              <div className="flex justify-between text-sm font-bold text-purple-800 border-t border-purple-200 pt-1 mt-1">
                <span>Stones Total</span><span>{fmt(estimate.stones_amount)}</span>
              </div>
            </div>
          )}

          {/* Making */}
          <div className="flex justify-between items-center bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
            <span className="text-sm text-orange-800">Making {estimate.making_charges_type === 'per_gram' ? `(${fmt(estimate.making_charges_per_gram)}/g)` : '(Fixed)'}</span>
            <span className="font-bold text-orange-900">{fmt(estimate.making_amount)}</span>
          </div>

          {/* Total */}
          <div className="bg-emerald-600 text-white rounded-lg px-4 py-3 flex justify-between items-center">
            <span className="font-semibold">TOTAL (All Inclusive)</span>
            <span className="text-2xl font-bold">{fmt(estimate.total_amount)}</span>
          </div>

          {/* Images */}
          {estimate.images?.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {estimate.images.map((url, i) => (
                <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="w-14 h-14 rounded-lg overflow-hidden border">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                </a>
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2 pt-2 border-t">
            <Button
              size="sm"
              variant="outline"
              onClick={handleViewEstimates}
              data-testid="popup-view-estimates-btn"
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
            >
              <List className="w-3 h-3 mr-1" /> View Estimates
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handlePrint}
              data-testid="popup-print-btn"
              className="border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              <Printer className="w-3 h-3 mr-1" /> Print
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleShare}
              disabled={isSharing}
              data-testid="popup-share-btn"
            >
              <Share2 className="w-3 h-3 mr-1" /> {isSharing ? 'Sharing…' : 'Share'}
            </Button>
            {onDuplicate && (
              <Button size="sm" variant="outline" onClick={() => { onOpenChange(false); onDuplicate(estimate); }} data-testid="popup-duplicate-btn">
                <Copy className="w-3 h-3 mr-1" /> Duplicate
              </Button>
            )}
            <Button size="sm" onClick={handleConfirm} disabled={confirming} className="bg-emerald-500 hover:bg-emerald-600" data-testid="popup-confirm-btn">
              <Check className="w-3 h-3 mr-1" /> {confirming ? 'Confirming...' : 'Confirm'}
            </Button>
            {estimate.status === 'confirmed' && (
              <Button size="sm" onClick={handleCreateOrder} className="bg-blue-600 hover:bg-blue-700" data-testid="popup-create-order-btn">
                <ShoppingCart className="w-3 h-3 mr-1" /> Create Order
              </Button>
            )}
            <Button size="sm" variant="destructive" onClick={handleDelete} disabled={deleting} data-testid="popup-delete-btn">
              <Trash2 className="w-3 h-3 mr-1" /> {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>
      </DialogContent>

      {/* Hidden off-screen EstimateCard for Share capture only */}
      {isSharing && (
        <div style={{ position: 'fixed', left: '-9999px', top: 0, zIndex: -1 }}>
          <EstimateCard ref={cardRef} estimate={estimate} />
        </div>
      )}
    </Dialog>
  );
};

export default EstimateDetailPopup;
